import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import FlexiOssCinematic from './components/FlexiOssCinematic';
import FlexiOssOrthoPage from './pages/FlexiOssOrthoPage';
import FlexiOssDentPage from './pages/FlexiOssDentPage';
import FlexiOssVetPage from './pages/FlexiOssVetPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import NdaPage from './pages/NdaPage';
import MedicalDisclaimerPage from './pages/MedicalDisclaimerPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import ReimbursementGuidePage from './pages/RvsReferencePage';
import TerritoryInsightsPage from './pages/TerritoryInsightsPage';
import FaqPage from './pages/FaqPage';
import FdaVerificationPage from './pages/FdaVerificationPage';
import CookieConsent from './components/FlexiOssCinematic/CookieConsent';
import FmsiPreloader from './components/FlexiOssCinematic/FmsiPreloader';
import BoneCursor from './components/FlexiOssCinematic/BoneCursor';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'sonner';
import Lenis from 'lenis';
import { SITE_URL } from './lib/siteConfig';
import './App.css';
import './light-theme.css';

function GATracker() {
  const location = useLocation();
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    }
  }, [location]);
  return null;
}

const SEO_ORIGIN = SITE_URL;
const PAGE_SEO = {
  '/': {
    title: 'FlexiOss Philippines | 3rd Generation Bone Regenerative Biomaterial',
    description: 'PH-FDA certified bioactive, osteoconductive bone substitute. FlexiOss is a 3rd generation hydroxyapatite + curdlan-polymer composite for orthopedic, dental and veterinary bone defect reconstruction — remodels into natural bone, no reoperation.',
  },
  '/flexioss-ortho': {
    title: 'FlexiOss® Ortho | Synthetic Bone Graft for Orthopedic & Trauma Surgery',
    description: 'FlexiOss® Ortho is a 100% synthetic, PH-FDA certified hydroxyapatite + curdlan bone graft for comminuted fractures and long-bone defects. Hydrates into a moldable, antibiotic-loadable scaffold that remodels into native bone.',
  },
  '/flexioss-dent': {
    title: 'FlexiOss® Dent | Bone Graft for Dental Implants & Maxillofacial Surgery',
    description: 'FlexiOss® Dent is a synthetic, biphasic bone graft for socket preservation, sinus lifts and ridge augmentation. PH-FDA certified, animal-free and moldable — supports predictable implant-site regeneration.',
  },
  '/flexioss-vet': {
    title: 'FlexiOss® Vet | Bone Regeneration for Veterinary Surgery',
    description: 'FlexiOss® Vet is a synthetic, osteoconductive bone graft for companion and equine patients — extraction defects, oronasal fistulae and deformity correction. Human-grade regenerative science for veterinary surgery.',
  },
  '/faq': {
    title: 'FlexiOss® Clinical FAQ | Hydroxyapatite Bone Graft Evidence',
    description: "Skeptical about hydroxyapatite bone substitutes? See the peer-reviewed evidence on FlexiOss's HAP + curdlan composite — moldability, resorption, granule migration, antibiotic loading and biocompatibility — with sources and stated limitations. For healthcare professionals.",
  },
  '/fda-verification': {
    title: 'FDA Verification & Institutional Compliance | FlexiOss® Philippines',
    description: 'Verify FlexiOss® medical device registration with the Philippines FDA (Ortho CMDR-2025-04943A, Dent CMDR-2025-04943B). Exclusively distributed by Fortschritt Medical Supplies Inc. Confirm our credentials on the National eGov Portal System.',
  },
  '/privacy-policy': { title: 'Privacy Policy | FlexiOss® Philippines', description: 'How FlexiOss® Philippines (Fortschritt Medical Supplies Inc.) collects, uses and protects your data under the Philippine Data Privacy Act of 2012.' },
  '/terms-of-service': { title: 'Terms of Service | FlexiOss® Philippines', description: 'The terms governing your access to and use of the FlexiOss® Philippines website and services.' },
  '/non-disclosure': { title: 'Non-Disclosure Agreement | FlexiOss® Philippines', description: 'Confidentiality and proprietary information protection for FlexiOss® technical, clinical and commercial data.' },
  '/medical-disclaimer': { title: 'Medical Disclaimer | FlexiOss® Philippines', description: 'Important safety, indication and usage information for FlexiOss® Class III medical devices. For healthcare professionals only.' },
  '/cookie-policy': { title: 'Cookie Policy | FlexiOss® Philippines', description: 'How FlexiOss® Philippines uses cookies and similar technologies on flexioss.ph.' },
};
const NOINDEX_PATHS = ['/territory-insights'];
const SELF_TITLED_PATHS = ['/reimbursement-guide', '/territory-insights'];
const INDEXABLE_PATHS = new Set([...Object.keys(PAGE_SEO), '/reimbursement-guide']);

// Keeps canonical / Open Graph / robots / titles correct per route in this SPA
// (previously the static homepage canonical was applied to every page).
function SeoManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    const url = SEO_ORIGIN + (pathname === '/' ? '/' : pathname);
    const seo = PAGE_SEO[pathname];
    const set = (sel, attr, val) => { const el = document.head.querySelector(sel); if (el && val != null) el.setAttribute(attr, val); };

    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement('link'); canon.setAttribute('rel', 'canonical'); document.head.appendChild(canon); }
    canon.setAttribute('href', url);

    set('meta[property="og:url"]', 'content', url);
    set('meta[name="twitter:url"]', 'content', url);

    if (seo && !SELF_TITLED_PATHS.includes(pathname)) {
      document.title = seo.title;
      set('meta[name="title"]', 'content', seo.title);
      set('meta[name="description"]', 'content', seo.description);
      set('meta[property="og:title"]', 'content', seo.title);
      set('meta[property="og:description"]', 'content', seo.description);
      set('meta[name="twitter:title"]', 'content', seo.title);
      set('meta[name="twitter:description"]', 'content', seo.description);
    }

    const robots = document.head.querySelector('meta[name="robots"]');
    if (robots) {
      const indexable = INDEXABLE_PATHS.has(pathname) && !NOINDEX_PATHS.includes(pathname);
      robots.setAttribute('content', indexable ? 'index, follow, max-image-preview:large, max-snippet:-1' : 'noindex, follow');
    }
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Premium momentum scroll on desktop only — mobile keeps native (snappier/responsive).
    if (coarse || reduced) return undefined;
    const lenis = new Lenis({
      duration: 1.05,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => { lenis.raf(time); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); window.__lenis = null; };
  }, []);
  return null;
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const lenis = typeof window !== 'undefined' ? window.__lenis : null;
    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      let tries = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          if (lenis) {
            lenis.scrollTo(el, { offset: -72, duration: 1.1 });
          } else {
            const top = el.getBoundingClientRect().top + window.scrollY - 72;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        } else if (tries++ < 25) {
          setTimeout(tryScroll, 60);
        }
      };
      setTimeout(tryScroll, 60);
    } else if (lenis) {
      lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <ScrollManager />
        <SmoothScroll />
        <FmsiPreloader />
        <GATracker />
        <SeoManager />
        <Routes>
          <Route path="/" element={<FlexiOssCinematic />} />
          <Route path="/flexioss-ortho" element={<FlexiOssOrthoPage />} />
          <Route path="/flexioss-dent" element={<FlexiOssDentPage />} />
          <Route path="/flexioss-vet" element={<FlexiOssVetPage />} />
          <Route path="/ortho" element={<Navigate to="/flexioss-ortho" replace />} />
          <Route path="/dent" element={<Navigate to="/flexioss-dent" replace />} />
          <Route path="/vet" element={<Navigate to="/flexioss-vet" replace />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/non-disclosure" element={<NdaPage />} />
          <Route path="/medical-disclaimer" element={<MedicalDisclaimerPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/reimbursement-guide" element={<ReimbursementGuidePage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/fda-verification" element={<FdaVerificationPage />} />
          <Route path="/territory-insights" element={<TerritoryInsightsPage />} />
          <Route path="/rvs-reference" element={<Navigate to="/reimbursement-guide" replace />} />
        </Routes>
        <CookieConsent />
      </Router>
      <BoneCursor />
      <Toaster position="top-center" richColors closeButton theme="dark" />
    </ThemeProvider>
  );
}

export default App;
